import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import React from 'react';
import { useProductActionsContext } from '../../context/ProductActionsProvider';
import { useCustomerContext } from '../../context/CustomerContextProvider';
import { ProductCarousel } from '../../ProductCarousel/ProductCarousel';
import { styles } from './EnhancedProductCarousel.css';
import { ProductCard } from '../../ProductCard';
import { AlgoliaHitTransformer } from '../../Algolia/transformers/AlgoliaHitTransformer';
import { InfoModule } from '../InfoModule';
import { BundleModule, BundleModuleProps } from '../BundleModule';
import { InfoModuleProps } from '../InfoModule/InfoModule';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { ProductCardVariant } from '../../ProductCard/ProductCard';
import { PossiblyResponsiveValue, useResponsiveValue } from '../../hooks/useResponsiveValue';

export type EnhancedProductCarouselProps = {
  products: Array<AlgoliaProductHit>;
  infoModuleProps?: InfoModuleProps;
  bundleModuleProps?: BundleModuleProps;
  numProducts?: number;
  className?: string;
  cardVariant?: PossiblyResponsiveValue<ProductCardVariant> | 'minified';
  trackingData?: {
    listName?: string;
    pageType?: string;
    group?: string;
    category?: string;
  };
  slidesOffsetBefore?: PossiblyResponsiveValue<number>;
  testId?: string;
};

export const EnhancedProductCarousel = ({
  products,
  infoModuleProps,
  bundleModuleProps,
  numProducts,
  className,
  cardVariant: cardVariantProp = 'landscape',
  trackingData,
  slidesOffsetBefore,
  testId,
}: EnhancedProductCarouselProps) => {
  const isMobile = !useBreakpoint('md', 'up');
  const responsiveVariant = useResponsiveValue(cardVariantProp);
  const cardVariant = cardVariantProp === 'minified' ? 'minified' : responsiveVariant;
  return (
    <div className={className}>
      {infoModuleProps ? <InfoModule {...infoModuleProps} className={styles.infoModule} /> : null}
      <div className={styles.carouselWrapper} data-testid={testId}>
        {isMobile && bundleModuleProps ? <BundleModule {...bundleModuleProps} /> : null}
        <ProductCarouselWrapped
          numProducts={numProducts}
          products={products}
          bundleModule={!isMobile && bundleModuleProps ? <BundleModule {...bundleModuleProps} /> : null}
          cardVariant={cardVariant}
          trackingData={trackingData}
          slidesOffsetBefore={slidesOffsetBefore}
        />
      </div>
    </div>
  );
};

const ProductCarouselWrapped = ({
  products,
  bundleModule,
  numProducts = 5,
  cardVariant,
  trackingData,
  slidesOffsetBefore: slidesOffsetBeforeProp = 2,
}: {
  products: Array<AlgoliaProductHit>;
  bundleModule?: React.ReactNode;
  numProducts?: number;
  cardVariant?: ProductCardVariant;
  trackingData?: EnhancedProductCarouselProps['trackingData'];
  slidesOffsetBefore?: PossiblyResponsiveValue<number>;
}) => {
  const { onAddToCart, productCartPropsOverrides = {} } = useProductActionsContext();
  const { isLoggedIn, handleUpdateWishlist, getIsInWishlist } = useCustomerContext();
  const slidesOffsetBefore = useResponsiveValue(slidesOffsetBeforeProp);

  return (
    <ProductCarousel
      className={styles.carousel}
      products={products}
      slidesPerGroup={1}
      extraSlideStart={bundleModule}
      maxNumProducts={numProducts}
      trackingListName={trackingData?.listName}
      shouldTrackImpressions
      slidesOffsetBefore={slidesOffsetBefore}
      renderProduct={({ hit, position }) => {
        return (
          <ProductCard
            {...AlgoliaHitTransformer.toProductCardProps(hit, {
              trackingDescriptor: {
                page: trackingData?.pageType ?? '',
                group: trackingData?.group,
                category: trackingData?.category,
                ecommerceExtraProps: {
                  list: `${trackingData?.listName ?? ''}${
                    hit?.google_tag_manager?.recommendationSource
                      ? `-${hit.google_tag_manager?.recommendationSource}`
                      : ''
                  }`,
                  position,
                },
              },
            })}
            {...productCartPropsOverrides}
            shouldHideCartOverlay
            variant={cardVariant}
            isLoggedIn={isLoggedIn}
            onUpdateWishlist={handleUpdateWishlist}
            getIsInWishlist={getIsInWishlist}
            key={hit.objectID}
            onAddToCart={
              onAddToCart
                ? (e, _sku, variantId, eventData) => onAddToCart(e, hit, variantId, eventData?.trackingDescriptor)
                : undefined
            }
          />
        );
      }}
    />
  );
};
